import { Button, CloseButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { IComparisonReportSettings } from '../../../../data/providers/settings/reports/comparisonReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { ISelectableTag } from '../../../../data/types';
import {
  createComparisonTemplate,
  updateComparisonTemplate,
} from '../../../../data/writers/settings/reports/comparisonReportSettings';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ErrorMessage } from '../../../shared/form/ErrorMessage';
import { TagSelect } from '../../../shared/form/selects/TagsSelect';
import { Step } from '../../../shared/form/Step';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateMetadata } from '../../utils/settings/TemplateMetadata';
import { ITemplateFormProps } from '../../utils/settings/types';
import { MetricForm } from './MetricForm';

export const TemplateForm = (props: ITemplateFormProps): React.ReactElement => {
  const [template, setTemplate] = React.useState<
    Partial<ISettingsTemplate<IComparisonReportSettings>>
  >(props.template as ISettingsTemplate<IComparisonReportSettings>);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  /**
   * @description handles change to the currently selected template's description
   * @param description
   */
  const templateDescriptionChangeHandler = (description: string): void => {
    setTemplate({
      ...template,
      description,
    });
  };

  /**
   * @description handles change to the currently selected template's name
   * @param name
   */
  const templateNameChangeHandler = (name: string): void => {
    setTemplate({
      ...template,
      name,
    });
  };

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (props.template && props.template?.id) {
        await updateComparisonTemplate(
          template as ISettingsTemplate<IComparisonReportSettings>,
          props.isSelected
        );
      } else {
        await createComparisonTemplate(template);
      }

      props.onClose();
    } catch (e) {
      setLoading(false);
      setError('Something went wrong saving the template.');
    }
  };

  /**
   * @description handles changes to selected metrics
   * @param mMetrics
   */
  const settingsChangeHandler = (
    mSettings: IComparisonReportSettings
  ): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        ...mSettings,
      },
    });
  };

  const targetTagsChangeHandler = (tags: ISelectableTag[]): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        target: {
          tags,
        },
      },
    });
  };

  const populationTagsChangeHandler = (tags: ISelectableTag[]): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        population: {
          tags,
        },
      },
    });
  };

  const isValid = React.useMemo((): boolean => {
    if (isEmpty(template?.name)) return false;
    if (isEmpty(template?.settings)) return false;

    return true;
  }, [JSON.stringify(template)]);

  return (
    <InnerPopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>
              {props.template
                ? `Edit ${props.template.name}`
                : 'Create Comparison Settings Template'}
            </h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading && <LinearLoading />}
          <Row shouldCollapse>
            <LeftColumn>
              <TemplateMetadata
                step={1}
                name={template?.name}
                description={template?.description}
                onTemplateDescriptionChange={templateDescriptionChangeHandler}
                onTemplateNameChange={templateNameChangeHandler}
              />
              <Row shouldCollapse>
                <LeftColumn>
                  <FormSection>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>3</Step>
                        <h6>Target Tags (optional)</h6>
                      </CardHeaderLeft>
                    </CardHeader>
                    <div style={{ padding: '10px' }}>
                      <ControlSpacer>
                        <ControlLabel>Tags</ControlLabel>
                        <TagSelect
                          value={template?.settings?.target?.tags}
                          onChange={targetTagsChangeHandler}
                          disabled={!template?.settings?.testType}
                        />
                      </ControlSpacer>
                    </div>
                  </FormSection>
                </LeftColumn>
                <RightColumn>
                  <FormSection>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>4</Step>
                        <h6>Comparison Tags (optional)</h6>
                      </CardHeaderLeft>
                    </CardHeader>
                    <div style={{ padding: '10px' }}>
                      <ControlSpacer>
                        <ControlLabel>Tags</ControlLabel>
                        <TagSelect
                          value={template?.settings?.population?.tags}
                          onChange={populationTagsChangeHandler}
                          disabled={!template?.settings?.testType}
                        />
                      </ControlSpacer>
                    </div>
                  </FormSection>
                </RightColumn>
              </Row>
            </LeftColumn>
            <RightColumn>
              <MetricForm
                startingStep={1}
                settings={template?.settings}
                onSettingsChange={settingsChangeHandler}
              />
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </CardFooterLeft>
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading || !isValid} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
